import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { Box, Grid, FormHelperText, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'

import { StateSchema } from '@/hooks/form/models'
import { BonusPeriodRecurrenceEnum } from '@/enums'
import { setTimeForToday } from '@/utils/date'

import { DatePickerCustomComponent } from '@/views/components'

type Props = {
  state: StateSchema
  handleOnChange: (name: string, value: any) => void
}

const DateRangePeriodComponent = ({ state, handleOnChange }: Props) => {
  const { t } = useTranslation()

  const [fullStartDate, setFullStartDate] = useState(state.startDate.value)

  const [fullEndDate, setFullEndDate] = useState(state.endDate.value)

  const currentDate: Date = new Date()

  const handleChangeStartDate = (date: Date) => {
    handleOnChange('startDate', date)
    const startDate = new Date(date)
    startDate.setHours(fullStartDate.getHours())
    startDate.setMinutes(fullStartDate.getMinutes())
    setFullStartDate(startDate)
  }

  const handleChangeEndDate = (date: Date) => {
    handleOnChange('endDate', date)
    const endDate = new Date(date)
    endDate.setHours(fullEndDate.getHours())
    endDate.setMinutes(fullEndDate.getMinutes())
    setFullEndDate(endDate)
  }

  const handleChangeStartTime = (time: string) => {
    const date = new Date(time)
    handleOnChange('startTime', `${date.getHours()}:${date.getMinutes()}:00`)
    if (date.toString() !== 'Invalid Date') {
      const startDate = new Date(fullStartDate)
      startDate.setHours(
        Number(date.getHours()),
        Number(date.getMinutes()),
        0,
        0,
      )
      setFullStartDate(date)
    }
  }

  const handleChangeEndTime = (time: string) => {
    const date = new Date(time)
    handleOnChange('endTime', `${date.getHours()}:${date.getMinutes()}:00`)
    if (date.toString() !== 'Invalid Date') {
      const endDate = new Date(fullEndDate)
      endDate.setHours(Number(date.getHours()), Number(date.getMinutes()), 0, 0)
      setFullEndDate(endDate)
    }
  }

  const setTimeValue = () => {
    const timeDateStart = setTimeForToday(state.startTime.value)
    const startDate = new Date(fullStartDate)
    startDate.setHours(
      Number(timeDateStart.getHours()),
      Number(timeDateStart.getMinutes()),
      0,
      0,
    )
    const timeDateEnd = setTimeForToday(state.endTime.value)
    const endDate = new Date(fullEndDate)
    endDate.setHours(
      Number(timeDateEnd.getHours()),
      Number(timeDateEnd.getMinutes()),
      0,
      0,
    )
    setFullStartDate(startDate)
    setFullEndDate(endDate)
  }

  useEffect(() => {
    setTimeValue()
  }, []) // eslint-disable-line

  return (
    <Box my={2} mb={1} maxWidth={400}>
      {state.recurrence.value !== BonusPeriodRecurrenceEnum.daily && (
        <>
          <Box pt={1}>
            <Grid
              container
              spacing={1}
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Grid item xs={12} lg={6}>
                <DatePickerCustomComponent
                  label={t('labels.startDate')}
                  value={
                    state.startDate.value
                      ? new Date(state.startDate.value)
                      : null
                  }
                  onChange={(date) => handleChangeStartDate(date)}
                  dayOfWeekFormatter={(day) => day}
                />
              </Grid>
              <Grid item xs={12} lg={1}>
                <Box mt={2} pl={0.5} pr={1}>
                  <Typography variant="caption">{t('labels.at')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box mt={1}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      views={['hours', 'minutes']}
                      label={t('labels.time')}
                      value={dayjs(setTimeForToday(state.startTime.value))}
                      onChange={(date) =>
                        handleChangeStartTime(date.toString())
                      }
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box pt={2}>
            <Grid
              container
              spacing={1}
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Grid item xs={12} lg={6}>
                <DatePickerCustomComponent
                  label={t('labels.endDate')}
                  minDate={new Date(state.startDate.value) || currentDate}
                  value={
                    state.endDate.value ? new Date(state.endDate.value) : null
                  }
                  onChange={(date) => handleChangeEndDate(date)}
                  dayOfWeekFormatter={(day) => day}
                />
                {fullStartDate > fullEndDate && (
                  <FormHelperText error={fullStartDate > fullEndDate}>
                    {t('messages.validateEndTime')}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} lg={1}>
                <Box mt={2} pl={0.5} pr={1}>
                  <Typography variant="caption">{t('labels.at')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box mt={1}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      views={['hours', 'minutes']}
                      label={t('labels.time')}
                      value={dayjs(setTimeForToday(state.endTime.value))}
                      onChange={(date) => handleChangeEndTime(date.toString())}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  )
}

export default DateRangePeriodComponent
